import { useCallback, useEffect, useMemo } from 'react';

import { SUBSCRIPTIONS_HIDDEN } from '@/pages/SettingsPage/components/settings-flags';
import { isManuallyGranted, planName } from '@/pages/SubscriptionSettingsPage/subscription-display';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import { useAuth } from '@/store/auth/auth.store';
import { usePermissions as usePermissionsStore } from '@/store/permissions/permissions.store';
import { useSubscriptionQuery } from '@/store/subscriptions/subscriptions.store';
import { isInOwnerGroup } from '@/utils/roles';

import { buildPermissionGates, planSeatOf } from './permission-gates';
import type { Permission } from './permissions.catalog';
import { type AccessVerdict, resolveAccess, resolveAnyAccess } from './plan-access';
import { usePermissions } from './usePermissions';

export interface AccessGate {
  /** `open` | `locked` | `hidden` — see `AccessVerdict`. An array is read as all-of. */
  verdictFor: (permissions: Permission | readonly Permission[]) => AccessVerdict;
  /**
   * The same question for a surface that opens on any *one* of the permissions named, which
   * is how most of the services' middlewares are written. See `resolveAnyAccess`.
   */
  anyVerdictFor: (permissions: readonly Permission[]) => AccessVerdict;
  /**
   * Whether the verdicts can be trusted yet.
   *
   * `hidden` is what a withheld permission reads as until the subscription and the seat
   * allow-lists arrive, because nothing can be attributed to the plan before then. That is
   * the safe answer for anything that only decides what to *draw* — the placeholder appears
   * a moment later. It is the wrong answer for anything irreversible: a route guard that
   * redirects on it unmounts the page for good, and the owner lands on the dashboard having
   * never seen why. Such callers must wait for this.
   *
   * Always true for a viewer who could never be told `locked` — there is nothing to wait for.
   */
  settled: boolean;
  /** The plan a locked surface is not on, ready to drop into copy: `Harbor`. */
  planName: string;
  /**
   * The plan is a lifetime one an admin granted. There is no higher plan to buy, so a locked
   * surface says so instead of offering an upgrade, and `upgradeHref` is `null`.
   */
  planGranted: boolean;
  /**
   * Sends the owner to the plans screen. `null` where this deployment ships no billing at
   * all — the lock still explains itself, it just has nowhere to point.
   */
  upgradeHref: string | null;
}

/** Where a locked surface sends the owner. Mirrors `TAB_PARAM` in `settings-anchors`. */
export const UPGRADE_HREF = '/settings?tab=subscriptions';

/**
 * One `GET /permissions/seats` per page load, shared by every caller of this hook.
 *
 * The hook runs in the nav and in every route guard at once, so an effect per consumer
 * would fan out into a burst of identical requests. The allow-lists are global — not
 * per-service — so a single fetch serves the whole session.
 *
 * A failure is deliberately not retried: with no allow-lists nothing can be attributed to
 * the plan, so every withheld permission reads as `hidden` and the app behaves exactly as
 * it did before any of this existed.
 */
let seatPermissionsRequest: Promise<unknown> | null = null;

const loadSeatPermissions = () => {
  seatPermissionsRequest ??= usePermissionsStore
    .getState()
    .fetchSeatPermissions()
    .catch((error: unknown) => {
      console.error('Failed to fetch seat permissions:', error);
    });
};

/**
 * How a surface should treat a permission the current user may not hold.
 *
 * One hook for the whole app: nav items, route guards and in-page sections all ask the same
 * question and differ only in what they render for each answer. See `AccessVerdict` for why
 * the owner gets a placeholder where everyone else gets nothing.
 *
 * Everything the plan verdict needs — the seat allow-lists, the subscription — is fetched
 * only for an owner. Nobody else can ever be told `locked`, so for crew and for
 * service-provider accounts this hook makes no requests at all.
 */
export const useAccessGate = (): AccessGate => {
  const { can } = usePermissions();
  const seatPermissions = usePermissionsStore((state) => state.seatPermissions);
  const seatsSettled = usePermissionsStore((state) => state.seatPermissionsSettled);
  const serviceType = useActiveAccount((state) => state.serviceType);
  const serviceId = useActiveAccount((state) => state.serviceId);
  const userRoles = useAuth((state) => state.session?.userRoles);

  // Owner *of the selected vessel*: a service-provider account has no vessel plan to
  // upgrade, so nothing there is ever plan-locked. `serviceId` is part of it because the
  // subscription is read through the `X-Service-Id` header — without one there is no plan
  // to ask about, and the query below would stay pending forever rather than answer.
  const isVesselOwner = serviceType === 'vessel' && !!serviceId && isInOwnerGroup(userRoles);

  const {
    data: subscription = null,
    isSuccess: planKnown,
    isError: planFailed,
  } = useSubscriptionQuery({ enabled: isVesselOwner });

  useEffect(() => {
    if (isVesselOwner) loadSeatPermissions();
  }, [isVesselOwner]);

  const gates = useMemo(() => {
    const planSeat = planSeatOf(subscription);
    return buildPermissionGates(seatPermissions, planSeat, planSeat);
  }, [seatPermissions, subscription]);

  /**
   * `planKnown` matters as much as ownership. `planSeatOf(null)` is the free tier, so acting
   * before the subscription lands would padlock half the app in front of a paying owner and
   * then take the padlocks away again. Until it answers — or if it never does — a withheld
   * permission reads as `hidden`, which is what the app did before any of this existed.
   */
  const verdictFor = useCallback(
    (permissions: Permission | readonly Permission[]) =>
      resolveAccess({
        permissions: Array.isArray(permissions) ? permissions : [permissions as Permission],
        can,
        isVesselOwner: isVesselOwner && planKnown,
        gates,
      }),
    [can, isVesselOwner, planKnown, gates],
  );

  // A surface that names no permission requires none of them: `open`, not the `hidden` an
  // empty list of verdicts would otherwise fold to.
  const anyVerdictFor = useCallback(
    (permissions: readonly Permission[]): AccessVerdict =>
      permissions.length === 0 ? 'open' : resolveAnyAccess(permissions.map((p) => verdictFor(p))),
    [verdictFor],
  );

  return {
    verdictFor,
    anyVerdictFor,
    // Nobody but the owner of the selected vessel is ever told `locked`, so for everyone
    // else the verdicts are final from the first render and there is nothing to wait for.
    settled: !isVesselOwner || (seatsSettled && (planKnown || planFailed)),
    planName: planName(subscription),
    planGranted: isManuallyGranted(subscription),
    upgradeHref: SUBSCRIPTIONS_HIDDEN || isManuallyGranted(subscription) ? null : UPGRADE_HREF,
  };
};
